import { authService } from '@/services/auth/auth-service';
import type { useAuthStore, useToastStore } from '@/stores';
import type { User } from '@/types';

import { CURRENT_TOUR_VERSION } from '../steps/tour-steps';
import { setDismissed } from '../utils/dismiss-flag';

// `setUser` in the auth store accepts a fully-formed User (never null).
// Pull the concrete signature so consumers calling
// `silentlyMarkSeen(user, setUser)` don't have to widen the parameter to
// `User | null` just to satisfy the callback shape.
type SetUser = (u: User) => void;

/**
 * Authoritative seen-version persist used by skip / done / whats-new-empty.
 * Sets the dismissed flag *before* the network call so a tab crash mid-PATCH
 * cannot replay the tour next session, optimistically updates local state,
 * then retries the PATCH once before toasting the user. Behaviour is kept
 * identical to the prior in-component definition so existing tests
 * (TourProvider.test "skip persists" / "retries PATCH once" / "writes the
 * dismissal flag before the PATCH") continue to pass.
 */
export const persistOnboardingSeenVersion = async (
  user: ReturnType<typeof useAuthStore.getState>['user'],
  setUser: SetUser,
  addToast: ReturnType<typeof useToastStore.getState>['addToast'],
): Promise<void> => {
  setDismissed(CURRENT_TOUR_VERSION);

  if (user && user.onboardingSeenVersion < CURRENT_TOUR_VERSION) {
    setUser({ ...user, onboardingSeenVersion: CURRENT_TOUR_VERSION });
  }

  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      await authService.markOnboardingSeenVersion(CURRENT_TOUR_VERSION);
      return;
    } catch {
      if (attempt === 2) {
        addToast('error', 'Failed to save tour status — try again later');
        return;
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
  }
};

/**
 * Silent variant for the auto-start path: the user already saw the tour
 * (dismissed flag set) but the prior PATCH failed and the server still
 * says version 0. We re-attempt once with no toast, no retry, no
 * localStorage writes (those happened last session). On failure we
 * silently leave the server out of sync — the next session will try
 * again.
 */
export const silentlyMarkSeen = async (
  user: ReturnType<typeof useAuthStore.getState>['user'],
  setUser: SetUser,
): Promise<void> => {
  if (!user) return;
  try {
    await authService.markOnboardingSeenVersion(CURRENT_TOUR_VERSION);
    setUser({ ...user, onboardingSeenVersion: CURRENT_TOUR_VERSION });
  } catch {
    // Best-effort. The dismissed flag still suppresses replay this session.
  }
};
